export interface ActionPayload {
  text: string
  shared_with_agent?: boolean
  shared_with_client?: boolean
  shared_with_admin?: boolean
  shared_with_consumer?: boolean
  // Present only on combined_to_client / combined_to_notary actions — the ids
  // of the earlier pending drafts this action absorbed.
  absorbed_action_ids?: string[]
  // Present only on combined_to_client / combined_to_notary actions — ISO8601
  // timestamps of the source comments this action absorbed (deduped, sorted
  // ascending server-side). Drives the day-grouped provenance header rendered
  // once above the card list ("Combines comments from Jul 17 at 9:04pm and
  // 10:15pm, and Jul 18 at 8:30am"). Absent on older payloads — no header
  // renders then; there is no badge fallback.
  absorbed_comment_timestamps?: string[]
}

export interface Action {
  id: string
  action_type: 'forward' | 'acknowledge' | 'combined_to_client' | 'combined_to_notary'
  action_payload: ActionPayload
}

// Per-card lifecycle. Each pending action gets its own card, and each card
// moves through this independently of any other card in the same popover.
export type CardState = 'draft' | 'sent' | 'declined'

// Sequential state machine for the legacy (pre-combine) forward+acknowledge
// pair. Only used by the legacy orchestrator — combined payloads use the
// per-card bucket flow instead and never construct this state.
export enum WorkflowState {
  FORWARD_DRAFT = 'forward_draft',
  FORWARD_SENT = 'forward_sent',
  ACKNOWLEDGE_DRAFT = 'acknowledge_draft'
}

export enum ErrorType {
  ALREADY_DECIDED = 'already_decided',
  RETRYABLE = 'retryable',
  FATAL = 'fatal'
}

export interface DraftAction extends Action {
  order_id: number
  created_at: string
  updated_at: string
  decided_at: string | null
  decision: 'approved' | 'edited' | 'declined' | null
}

export const isDraftActionDecided = (action: DraftAction): boolean => {
  return action.decision !== null
}

export const WORKFLOW_CONSTANTS = {
  TRANSITION_DELAY_MS: 2000,
  MAX_RETRY_ATTEMPTS: 3,
  ALL_CARDS_DECIDED_MESSAGE: 'All done.',
  SUCCESS_MESSAGE_FORWARD: 'Message was sent.',
  SUCCESS_SUBTITLE_FORWARD: 'Generating a response to sender.',
  ERROR_MESSAGE_ALREADY_DECIDED: 'A response to this message was already sent.',
  ERROR_MESSAGE_RETRYABLE: 'An error occurred. Please try again.',
  ERROR_MESSAGE_FATAL: 'An error occurred. Please try creating the comment manually.'
} as const
